/* eslint-disable no-param-reassign */
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';

import { Box, useTheme } from '@chakra-ui/react';
import {
  emToPxInt,
  loadImage,
  urlFor,
} from '../../utils/generateSanityImageUrl';

/**
 * A background image component that can dynamically adjust its appearance based on breakpoints and image properties.
 *
 * @component
 * @param {object} props - The component's properties.
 * @param {object} props.children - The content to display within the component.
 * @param {object} props.image - The image object with asset information and hotspot.
 * @param {function} [props.gradientGenerator] - A function that generates gradients for different breakpoints.
 * @param {object} [props.breakpointOverrides] - Override gradient values for specific breakpoints.
 * @param {number} [props.maxImageWidth] - The maximum width for the displayed image.
 * @returns {JSX.Element} The rendered component.
 */
export default function SanityBackgroundImage({
  children,
  image,
  gradientGenerator = url => url,
  breakpointOverrides,
  maxImageWidth,
  ...props
}) {
  const theme = useTheme();
  const [imageIntrinsicRatio, setImageIntrinsicRatio] = useState(1);

  useEffect(() => {
    (async () => {
      if (typeof window !== 'undefined' && image?.asset?.url) {
        const img = new Image();
        const imgElem = await loadImage(
          urlFor(image.asset.url).auto('format').url(),
          img
        );
        const ratio = imgElem.width / imgElem.height;
        setImageIntrinsicRatio(ratio);
      }
    })();
  }, [image]);

  /**
   * Generates background images for different breakpoints.
   *
   * @returns {object} An object containing background image URLs for various breakpoints.
   */
  const generateBgImage = () =>
    Object.entries(theme.breakpoints).reduce((bgImage, [bp, em]) => {
      // eslint-disable-next-line no-param-reassign
      bgImage[bp] = breakpointOverrides[bp]
        ? breakpointOverrides[bp]
        : gradientGenerator(
            urlFor(
              image?.asset?.url ?? process.env.GATSBY_SANITY_FALLBACK_IMAGE_URL
            )
              .width(
                bp === '2xl'
                  ? maxImageWidth
                  : Math.max(
                      Math.ceil(400 * (imageIntrinsicRatio + 0.5)),
                      Math.min(emToPxInt(em), maxImageWidth)
                    )
              )
              .auto('format')
              .url()
          );
      return bgImage;
    }, {});

  return (
    <Box
      pos="relative"
      className="sanity-background-image"
      bgImage={generateBgImage()}
      backgroundPosition={`${image?.hotspot?.x ? image.hotspot.x * 100 : 50}% ${
        image?.hotspot?.y ? image.hotspot.y * 100 : 50
      }%`}
      bgSize="cover"
      bgRepeat="no-repeat"
      role="presentation"
      {...props}
    >
      {children}
    </Box>
  );
}

SanityBackgroundImage.defaultProps = {
  children: null,
  gradientGenerator: url => url,
  breakpointOverrides: {},
  maxImageWidth: 3600,
  image: {
    asset: {
      url: process.env.GATSBY_SANITY_FALLBACK_IMAGE_URL,
    },
  },
};

export const imageShape = PropTypes.shape({
  hotspot: PropTypes.shape({
    x: PropTypes.number,
    y: PropTypes.number,
  }),
  asset: PropTypes.shape({
    url: PropTypes.string.isRequired,
  }).isRequired,
});

SanityBackgroundImage.propTypes = {
  image: imageShape,
  children: PropTypes.node,
  gradientGenerator: PropTypes.func,
  breakpointOverrides: PropTypes.shape({
    base: PropTypes.string,
    sm: PropTypes.string,
    md: PropTypes.string,
    lg: PropTypes.string,
    xl: PropTypes.string,
    '2xl': PropTypes.string,
  }),
  maxImageWidth: PropTypes.number,
};
